<script lang="ts">
  /**
   * Modexp Visual — renders the current step of the square-and-multiply animation.
   *
   * Composed entirely from animation/primitives. The visual is a pure function
   * of the current step + the static inputs (base/exp/mod).
   *
   * Layout:
   *   1. Static input expression at the top (base^exp mod m, with proper superscript)
   *   2. Headline showing the current operation
   *   3. Binary row with active bit indicator
   *   4. Single dynamic state box (result) — the only thing that changes
   */
  import type { ModexpInputs, ModexpStep } from './types';
  import BinaryRow from '../../animation/primitives/BinaryRow.svelte';
  import StateBox from '../../animation/primitives/StateBox.svelte';

  type Props = {
    step: ModexpStep;
    inputs: ModexpInputs;
  };

  let { step, inputs }: Props = $props();

  // Derived display state — the visual reads from these.
  const bits = $derived(inputs.exp.toString(2));

  const activeBitIndex = $derived.by(() => {
    if (step.kind === 'inspect-bit' || step.kind === 'square' || step.kind === 'multiply') {
      return step.bitIndex;
    }
    return -1;
  });

  const processedBitIndices = $derived.by(() => {
    if (step.kind === 'inspect-bit' || step.kind === 'square' || step.kind === 'multiply') {
      return Array.from({ length: step.bitIndex }, (_, i) => i);
    }
    if (step.kind === 'done') {
      return Array.from({ length: bits.length }, (_, i) => i);
    }
    return [];
  });

  const resultDisplay = $derived.by(() => {
    if (
      step.kind === 'inspect-bit' ||
      step.kind === 'square' ||
      step.kind === 'multiply' ||
      step.kind === 'done'
    ) {
      return step.result;
    }
    return 1n;
  });

  // Headline operation — varies by step kind. Returned as a structured object so
  // the template can render parts with proper typography (sup, etc).
  type Headline =
    | { kind: 'expression'; base: bigint; exp: bigint; mod: bigint }
    | { kind: 'binary'; bits: string; exp: bigint }
    | { kind: 'init'; result: bigint }
    | { kind: 'bit'; index: number; bit: '0' | '1' }
    | { kind: 'square'; previous: bigint; result: bigint; mod: bigint }
    | { kind: 'multiply'; previous: bigint; base: bigint; result: bigint; mod: bigint }
    | { kind: 'done'; base: bigint; exp: bigint; mod: bigint; result: bigint };

  const headline: Headline = $derived.by(() => {
    switch (step.kind) {
      case 'start':
        return { kind: 'expression', base: inputs.base, exp: inputs.exp, mod: inputs.mod };
      case 'binary':
        return { kind: 'binary', bits, exp: inputs.exp };
      case 'inspect-bit':
        return step.bitIndex === 0
          ? { kind: 'init', result: step.result }
          : { kind: 'bit', index: step.bitIndex, bit: step.bit };
      case 'square':
        return { kind: 'square', previous: step.previousResult, result: step.result, mod: inputs.mod };
      case 'multiply':
        return { kind: 'multiply', previous: step.previousResult, base: inputs.base, result: step.result, mod: inputs.mod };
      case 'done':
        return { kind: 'done', base: inputs.base, exp: inputs.exp, mod: inputs.mod, result: step.result };
    }
  });

  // Stable string key for the {#key} block to retrigger the headline animation.
  // Avoid JSON.stringify because the object contains BigInts which can't be serialized.
  const headlineKey = $derived.by(() => {
    const h = headline;
    switch (h.kind) {
      case 'expression':
        return `expr:${h.base}^${h.exp}%${h.mod}`;
      case 'binary':
        return `bin:${h.bits}`;
      case 'init':
        return `init:${h.result}`;
      case 'bit':
        return `bit:${h.index}:${h.bit}`;
      case 'square':
        return `sq:${h.previous}->${h.result}`;
      case 'multiply':
        return `mul:${h.previous}->${h.result}`;
      case 'done':
        return `done:${h.result}`;
    }
  });
</script>

<div class="modexp-visual">
  <div class="input-expression" aria-label="Computing">
    <span class="input-base">{inputs.base}</span><sup class="input-exp">{inputs.exp}</sup>
    <span class="input-mod">mod {inputs.mod}</span>
  </div>

  <div class="headline" data-kind={headline.kind}>
    {#key headlineKey}
      <div class="headline-text">
        {#if headline.kind === 'expression'}
          <span class="muted">Computing&nbsp;</span>
          <span>{headline.base}</span><sup>{headline.exp}</sup>
          <span class="muted">&nbsp;mod {headline.mod}</span>
        {:else if headline.kind === 'binary'}
          <span class="muted">Binary:&nbsp;</span>
          <span class="bits">{headline.bits}</span>
          <span class="muted">&nbsp;= {headline.exp}</span>
        {:else if headline.kind === 'init'}
          <span class="muted">Initialize result =&nbsp;</span>
          <span class="emphasized">{headline.result}</span>
        {:else if headline.kind === 'bit'}
          <span class="muted">bit {headline.index + 1} =&nbsp;</span>
          <span class="emphasized">{headline.bit}</span>
        {:else if headline.kind === 'square'}
          <span>{headline.previous}</span><sup>2</sup>
          <span class="muted">&nbsp;mod {headline.mod} =&nbsp;</span>
          <span class="emphasized">{headline.result}</span>
        {:else if headline.kind === 'multiply'}
          <span>{headline.previous}</span>
          <span class="muted">&nbsp;×&nbsp;</span>
          <span>{headline.base}</span>
          <span class="muted">&nbsp;mod {headline.mod} =&nbsp;</span>
          <span class="emphasized">{headline.result}</span>
        {:else if headline.kind === 'done'}
          <span>{headline.base}</span><sup>{headline.exp}</sup>
          <span class="muted">&nbsp;mod {headline.mod} =&nbsp;</span>
          <span class="emphasized done">{headline.result}</span>
        {/if}
      </div>
    {/key}
  </div>

  <BinaryRow {bits} activeIndex={activeBitIndex} processedIndices={processedBitIndices} />

  <div class="state-row">
    <StateBox label="result" value={resultDisplay} />
  </div>
</div>

<style>
  .modexp-visual {
    display: flex;
    flex-direction: column;
    gap: var(--space-xl);
    align-items: center;
    width: 100%;
    max-width: 640px;
  }

  .input-expression {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    color: var(--ink-muted);
    letter-spacing: 0.01em;
  }

  .input-exp {
    font-size: 0.75em;
    vertical-align: super;
    line-height: 0;
  }

  .input-mod {
    margin-left: 6px;
  }

  .headline {
    min-height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .headline-text {
    font-family: var(--font-mono);
    font-size: var(--text-2xl);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    text-align: center;
    animation: headline-in 280ms ease;
    line-height: 1.3;
    word-break: break-word;
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0;
  }

  .headline-text sup {
    font-size: 0.6em;
    vertical-align: super;
    line-height: 0;
  }

  .headline-text .muted {
    color: var(--ink-muted);
    font-size: 0.85em;
  }

  .headline-text .emphasized {
    color: var(--accent);
    font-weight: var(--weight-medium);
  }

  .headline-text .emphasized.done {
    font-weight: var(--weight-bold);
    font-size: 1.05em;
  }

  .headline-text .bits {
    font-weight: var(--weight-medium);
    letter-spacing: 0.1em;
  }

  @keyframes headline-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .state-row {
    display: flex;
    gap: var(--space-md);
    flex-wrap: wrap;
    justify-content: center;
  }

  @media (max-width: 600px) {
    .headline-text {
      font-size: var(--text-xl);
    }
    .state-row {
      flex-direction: column;
      width: 100%;
      align-items: center;
    }
  }
</style>
